import { BaseAPI } from 'API/BaseApi';
import { eAPITags } from 'API/eAPITags';
import { eAccountType } from 'types/order/escrow/IAchAccountData';
import { IACHDisbursementFormData } from 'types/order/escrow/achDisbursements/IACHDisbursementFormData';
import {
  IAchAccountResponse,
  IAchRoutingNumberResponse,
} from 'types/accounting/response/IEscrowExpectedAchPullDepositResponse';
import { ICreateAchAccount } from 'types/order/escrow/achDisbursements/ICreateACHAccount';
import { ICreateACHDisbursement } from 'types/order/escrow/achDisbursements/ICreateACHDisbursement';
import { IUpdateACHDisbursement } from 'types/order/escrow/achDisbursements/IUpdateACHDisbursement';
import { emptyFieldsToUndefined } from './EscrowBaseAPI';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';
import { IAchPushDisbursementResponse } from 'types/order/escrow/response/IAchPushDisbursementResponse';
import { IACHDisbursementVoidRequest } from 'types/accounting/IACHDisbursementVoidRequest';

const AchDisbursementAPI = BaseAPI.injectEndpoints({
  endpoints: (build) => ({
    getACHDisbursement: build.query<IACHDisbursementFormData, { achId: string }>({
      query: ({ achId }) => ({
        url: `/timber/api/internal/escrow/ach_push_disbursements/${achId}`,
        method: 'GET',
      }),
      transformResponse: (ach: IAchPushDisbursementResponse) => ({
        ...ach,
        account: {
          ...ach.account,
          name: ach.account?.name ?? '',
          accountNumber: ach.account?.accountNumber ?? '',
          accountNumberConfirmation: ach.account?.accountNumber ?? '',
          routingNumber: ach.account?.routingNumber ?? '',
          accountType: ach.account?.accountType ?? eAccountType.CCD,
        },
        type: eDisbursementType.ACH,
      }),
      providesTags: (result, error, { achId }) => [{ type: eAPITags.ACHDisbursement, id: achId }],
    }),
    createAchDisbursement: build.mutation<void, ICreateACHDisbursement>({
      query: ({ escrowFileId, ach }) => ({
        url: `/timber/api/internal/escrow/${escrowFileId}/ach_push_disbursements`,
        method: 'POST',
        body: emptyFieldsToUndefined(ach),
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
      ],
    }),
    updateAchDisbursement: build.mutation<void, IUpdateACHDisbursement>({
      query: ({ ach: { id, ...body } }) => ({
        url: `/timber/api/internal/escrow/ach_push_disbursements/${id}`,
        method: 'PUT',
        body: emptyFieldsToUndefined(body),
      }),
      invalidatesTags: (result, error, { orderId, ach: { id } }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.ACHDisbursement, id },
      ],
    }),
    deleteAchDisbursement: build.mutation<void, { achId: string; orderId: string }>({
      query: ({ achId }) => ({
        url: `/timber/api/internal/escrow/ach_push_disbursements/${achId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { orderId, achId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.ACHDisbursement, id: achId },
      ],
    }),
    voidACHDisbursement: build.mutation<void, { orderId: string; ach: IACHDisbursementVoidRequest }>({
      query: ({ ach }) => ({
        url: `/timber/api/internal/escrow/ach_push_disbursements/void`,
        method: 'POST',
        body: ach,
      }),
      invalidatesTags: (result, error, { orderId, ach }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.ACHDisbursement, id: ach.id },
      ],
    }),
    sendAchDisbursement: build.mutation<void, { achId: string; orderId: string }>({
      query: ({ achId }) => ({
        url: `/timber/api/internal/escrow/ach_push_disbursements/${achId}/send`,
        method: 'PATCH',
      }),
      invalidatesTags: (result, error, { orderId, achId }) => [
        { type: eAPITags.DetailedEscrowData, id: orderId },
        { type: eAPITags.SectionData, id: orderId },
        { type: eAPITags.ACHDisbursement, id: achId },
      ],
    }),
    getACHAccounts: build.query<IAchAccountResponse[], void>({
      query: () => ({
        url: `/timber/api/internal/escrow/ach_accounts`,
        method: 'GET',
      }),
      providesTags: [{ type: eAPITags.ACHAccountInformation, id: 'LIST' }],
    }),
    getACHRoutingNumber: build.query<IAchRoutingNumberResponse, string>({
      query: (achRoutingNumber) => ({
        url: `/timber/api/internal/escrow/federal-bank`,
        params: { achRoutingNumber: achRoutingNumber },
        method: 'GET',
      }),
    }),
    createACHAccount: build.mutation<IAchAccountResponse, ICreateAchAccount>({
      query: (body) => ({
        url: `/timber/api/internal/escrow/ach_accounts`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: eAPITags.ACHAccountInformation, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetACHDisbursementQuery,
  useCreateAchDisbursementMutation,
  useDeleteAchDisbursementMutation,
  useUpdateAchDisbursementMutation,
  useSendAchDisbursementMutation,
  useGetACHAccountsQuery,
  useGetACHRoutingNumberQuery,
  useCreateACHAccountMutation,
  useVoidACHDisbursementMutation,
} = AchDisbursementAPI;
